/**
 * Cleanup Old localStorage Keys
 * Removes old separate data patterns that have been consolidated into the unified structure
 */

// Import getCurrentBusinessId from sharedCore (centralized)
import { getCurrentBusinessId } from './sharedCore';
import { listKvKeys, kvGetRaw, kvRemove } from '../infrastructure/storage/kvAccess';
import { isCleanupProtectedKey } from './storageProtectedKeys';
import { isLegacyDiscardKvKey } from '../infrastructure/storage/legacyDiscardKvKey';

/**
 * Clean up old separate localStorage keys
 * This should be called once after migration to remove old data patterns
 */
export const cleanupOldKeys = () => {
  try {
    const businessId = getCurrentBusinessId();
    if (!businessId) {
      console.warn('No business ID found, skipping cleanup');
      return { success: false, error: 'No business ID found' };
    }

    let cleanedKeys = 0;
    const keysToRemove = [];

    // Get all localStorage keys
    listKvKeys().forEach((key) => {
      if (!key || isCleanupProtectedKey(key)) return;

      // Remove old stock data patterns
      if (key.startsWith('stock_data_')) {
        keysToRemove.push(key);
        cleanedKeys++;
      }

      // Remove old lots patterns
      if (key.startsWith('lots_')) {
        keysToRemove.push(key);
        cleanedKeys++;
      }

      // Remove old persistent stock data
      if (key === 'persistent_stock_data') {
        keysToRemove.push(key);
        cleanedKeys++;
      }

      // Remove old additional stock locations
      if (key === 'additional_stock_locations') {
        keysToRemove.push(key);
        cleanedKeys++;
      }

      // Remove old cleanup flags
      if (key === 'main_stock_cleaned' || key === 'product_id_stocks_cleaned') {
        keysToRemove.push(key);
        cleanedKeys++;
      }

      // Remove old offline queue (if it exists as separate key)
      if (key === 'offline_queue') {
        keysToRemove.push(key);
        cleanedKeys++;
      }

      // High-volume service cache (rebuilds on demand)
      if (key.startsWith('hvs_')) {
        keysToRemove.push(key);
        cleanedKeys++;
      }

      // Legacy InstantCache full dumps (OOM on boot if rehydrated)
      if (isLegacyDiscardKvKey(key)) {
        keysToRemove.push(key);
        cleanedKeys++;
      }

      // Remove old currentBusiness and currentUser (keep for compatibility)
      // Note: We keep these for now as they might be used by other parts of the system
      // if (key === 'currentBusiness' || key === 'currentUser') {
      //   keysToRemove.push(key);
      //   cleanedKeys++;
      // }
    });

    // Remove all identified keys
    keysToRemove.forEach((key) => {
      try {
        kvRemove(key);
      } catch (error) {
        console.error(`Error removing key ${key}:`, error);
      }
    });

    console.log(`✅ Cleaned up ${cleanedKeys} old localStorage keys`);
    return { success: true, cleanedKeys };
  } catch (error) {
    console.error('Error during cleanup:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Check if cleanup is needed
 */
export const needsCleanup = () => {
  try {
    // Check for old patterns
    const keys = listKvKeys();
    const hasOldStockData = keys.some((key) => key.startsWith('stock_data_'));
    const hasOldLots = keys.some((key) => key.startsWith('lots_'));
    const hasOldPersistent = kvGetRaw('persistent_stock_data') != null;
    const hasOldLocations = kvGetRaw('additional_stock_locations') != null;
    const hasOldFlags = kvGetRaw('main_stock_cleaned') != null ||
      kvGetRaw('product_id_stocks_cleaned') != null;
    const hasOfflineQueue = kvGetRaw('offline_queue') != null;
    const hasHvsCache = keys.some((key) => key.startsWith('hvs_'));
    const hasInstantCacheDump = keys.some((key) => isLegacyDiscardKvKey(key));

    return (
      hasOldStockData ||
      hasOldLots ||
      hasOldPersistent ||
      hasOldLocations ||
      hasOldFlags ||
      hasOfflineQueue ||
      hasHvsCache ||
      hasInstantCacheDump
    );
  } catch (error) {
    console.error('Error checking cleanup needs:', error);
    return false;
  }
};
